import { z } from 'zod';
import { addClassMembersSchema } from './scheduling';

// ── Subject ─────────────────────────────────────────────────────────────────
export const createSubjectSchema = z.object({
  name: z.string().min(1, 'Tên môn học là bắt buộc').max(100),
  defaultRate: z.number().positive().nullable().optional(),
  defaultMonthlyRate: z.number().min(0).nullable().optional(),
});
export const updateSubjectSchema = createSubjectSchema.partial().extend({
  /** THI-346: the per-subject session-rating switch. */
  sessionRatingEnabled: z.boolean().optional(),
});

// ── Student ─────────────────────────────────────────────────────────────────
const payerSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('existing'), clientId: z.string().min(1, 'Chọn người trả phí') }),
  z.object({
    mode: z.literal('new'),
    name: z.string().min(1, 'Tên người trả phí là bắt buộc'),
    email: z.string().email().nullable().optional(),
    zaloPhone: z.string().nullable().optional(),
  }),
]);

export const createStudentSchema = z.object({
  name: z.string().min(1, 'Họ và tên là bắt buộc').max(100),
  nickname: z.string().max(100).nullable().optional(),
  tags: z.array(z.string().min(1).max(40)).optional(),
  // Refined, not bare: both write paths call `new Date(...)` on this, and an
  // unparseable string became an Invalid Date that Prisma rejected — surfacing
  // as a 500 for what is plainly a bad request. The refinement keeps every
  // format the callers actually send ('2014-04-01' and full ISO alike).
  // Deliberately NOT applied to importCommitSchema: the import treats an
  // unreadable date as absent so one bad cell cannot fail a whole roster.
  dateOfBirth: z
    .string()
    .refine((v) => !Number.isNaN(new Date(v).getTime()), 'Ngày sinh không hợp lệ')
    .nullable()
    .optional(),
  gender: z.enum(['MALE', 'FEMALE']).nullable().optional(),
  notes: z.string().nullable().optional(),
  payer: payerSchema,
});

export const updateStudentSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  nickname: z.string().max(100).nullable().optional(),
  tags: z.array(z.string().min(1).max(40)).optional(),
  // Refined, not bare: both write paths call `new Date(...)` on this, and an
  // unparseable string became an Invalid Date that Prisma rejected — surfacing
  // as a 500 for what is plainly a bad request. The refinement keeps every
  // format the callers actually send ('2014-04-01' and full ISO alike).
  // Deliberately NOT applied to importCommitSchema: the import treats an
  // unreadable date as absent so one bad cell cannot fail a whole roster.
  dateOfBirth: z
    .string()
    .refine((v) => !Number.isNaN(new Date(v).getTime()), 'Ngày sinh không hợp lệ')
    .nullable()
    .optional(),
  gender: z.enum(['MALE', 'FEMALE']).nullable().optional(),
  notes: z.string().nullable().optional(),
  status: z.enum(['ACTIVE', 'INACTIVE']).optional(),
  clientId: z.string().min(1).optional(),
});

// ── Enrollment ──────────────────────────────────────────────────────────────
const enrollmentBase = {
  studentId: z.string().min(1),
  subjectId: z.string().min(1),
  startedAt: z.string().optional(),
  notes: z.string().nullable().optional(),
};

export const createEnrollmentSchema = z.discriminatedUnion('pricingType', [
  z.object({
    ...enrollmentBase,
    pricingType: z.literal('PER_SESSION'),
    ratePerSession: z.number().positive(),
    chargeAbsent: z.boolean().optional(),
    billingPeriod: z.enum(['MONTHLY', 'TERM']).default('MONTHLY'),
    termSize: z.number().int().positive().optional(),
  }),
  z.object({ ...enrollmentBase, pricingType: z.literal('MONTHLY_FLAT'), monthlyRate: z.number().positive() }),
]).refine(
  (v) => v.pricingType !== 'PER_SESSION' || v.billingPeriod !== 'TERM' || (v.termSize != null && v.termSize > 0),
  { message: 'termSize bắt buộc khi billingPeriod = TERM', path: ['termSize'] },
);

export const updateEnrollmentSchema = z.object({
  status: z.enum(['ACTIVE', 'PAUSED', 'ENDED']).optional(),
  endedAt: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  chargeAbsent: z.boolean().optional(),
  ratePerSession: z.number().positive().optional(),
  monthlyRate: z.number().positive().optional(),
});

// ISO date/datetime the TERM should start on. Optional; server defaults to
// now() when omitted (see startTerm in invoicingService).
export const startTermSchema = z.object({
  startDate: z.string().min(1).optional(),
});

// POST /enrollments/charge-absent/bulk — re-apply the absence-billing rule to
// every existing PER_SESSION enrollment at once (see routes/enrollments.ts).
export const chargeAbsentBulkSchema = z.object({
  chargeAbsent: z.boolean(),
});

// ── Student import (AI Excel/CSV) ────────────────────────────────────────────
const importFieldMappingSchema = z.object({
  studentName: z.number().int().nullable(),
  nickname: z.number().int().nullable(),
  dateOfBirth: z.number().int().nullable(),
  tags: z.number().int().nullable(),
  payerName: z.number().int().nullable(),
  payerPhone: z.number().int().nullable(),
  payerEmail: z.number().int().nullable(),
});

// Sent by the FE when the user corrects/confirms the AI-detected mapping — the
// analyze route then skips the LLM call and uses this directly.
export const importAnalyzeOverrideSchema = z
  .object({
    headerRowIndex: z.number().int().min(0),
    payerMode: z.enum(['parent', 'self_pay']),
    mapping: importFieldMappingSchema,
  })
  .partial()
  .optional();

// THI-267 Task 7 — importing with a class as context. Longhand, additive:
// every field below is new and optional, so a commit that never sets them
// (the plain Excel import) parses byte-identical to before. `classId`
// present requires `startDate` and `pricing` (refine below); per-student
// `rate`/`useExistingStudentId`/`slotIds` mirror the batch-add row shape
// (`addClassMembersSchema`'s row base) so `commitStudentImport` can hand
// every row straight to `addClassMembers` without a second implementation of
// its per-row logic. `pricing` reuses `addClassMembersSchema`'s own
// sub-schema object (its TERM refine included) rather than a redefinition
// that could drift from it.
export const importCommitSchema = z
  .object({
    classId: z.string().min(1).optional(),
    startDate: addClassMembersSchema.shape.startDate.optional(),
    pricing: addClassMembersSchema.shape.pricing.optional(),
    students: z
      .array(
        z.object({
          studentName: z.string().min(1),
          nickname: z.string().nullable().optional(),
          dateOfBirth: z.string().nullable().optional(),
          tags: z.array(z.string()).default([]),
          payer: z.object({
            name: z.string().min(1),
            phone: z.string().nullable().optional(),
            email: z.string().nullable().optional(),
          }),
          // Class-context only; a plain commit never sets these (enforced by
          // the refine below, fix round 1 Important-4). `rate` optional, same
          // as the batch add's own row (fix round 1 Ruling 1): when omitted,
          // `addClassMembers` falls back to the class's, then the subject's,
          // default rate for the batch's pricingType.
          rate: z.number().positive().optional(),
          useExistingStudentId: z.string().min(1).optional(),
          slotIds: z.array(z.string().min(1)).optional(),
        }),
      )
      .min(1)
      .max(500),
  })
  .refine((v) => v.classId === undefined || (v.startDate !== undefined && v.pricing !== undefined), {
    message: 'startDate và pricing bắt buộc khi có classId',
    path: ['classId'],
  })
  // Fix round 1 (Ruling 2) — a class-context row does real, sequential work
  // (student/payer create, then the whole addClassMembers pass, then N slot
  // joins) inside one Serializable/20s transaction; 500 of those is not
  // achievable in the budget (review's timing count: ~21 statements per
  // two-slot row). The plain import keeps 500 (ruling 4 — it has its own
  // plan gate and credit, and does none of that per-row work).
  .refine((v) => v.classId === undefined || v.students.length <= 100, {
    message: 'Nhập vào lớp tối đa 100 học viên một lần.',
    path: ['students'],
  })
  // Fix round 1 (Important-4) — without a classId these fields would parse,
  // get silently ignored (no student created, nothing linked), and the
  // response would still say `success: true` — a false "it worked" for a
  // dialog that thinks it just added someone to a class or set their price.
  .refine(
    (v) =>
      v.classId !== undefined ||
      (v.startDate === undefined &&
        v.pricing === undefined &&
        v.students.every((s) => s.rate === undefined && s.useExistingStudentId === undefined && s.slotIds === undefined)),
    {
      message: 'startDate, pricing, rate, useExistingStudentId và slotIds chỉ dùng khi có classId',
      path: ['classId'],
    },
  );

export type ImportAnalyzeOverride = z.infer<typeof importAnalyzeOverrideSchema>;
export type ImportCommitInput = z.infer<typeof importCommitSchema>;

export type CreateSubjectInput = z.infer<typeof createSubjectSchema>;
export type UpdateSubjectInput = z.infer<typeof updateSubjectSchema>;
export type CreateStudentInput = z.infer<typeof createStudentSchema>;
export type UpdateStudentInput = z.infer<typeof updateStudentSchema>;
export type CreateEnrollmentInput = z.infer<typeof createEnrollmentSchema>;
export type UpdateEnrollmentInput = z.infer<typeof updateEnrollmentSchema>;
export type StartTermInput = z.infer<typeof startTermSchema>;
export type ChargeAbsentBulkInput = z.infer<typeof chargeAbsentBulkSchema>;
